import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calculator, Receipt, ShieldCheck, FileText, Scale, GraduationCap,
  Target, Code2, Search, Workflow, TrendingUp, Rocket, Users, Truck,
  ClipboardCheck, ArrowUpRight,
} from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  Calculator, Receipt, ShieldCheck, FileText, Scale, GraduationCap,
  Target, Code2, Search, Workflow, TrendingUp, Rocket, Users, Truck, ClipboardCheck,
};

const categories = ['All', 'Finance', 'Compliance', 'Strategy', 'Technology', 'Operations', 'Development', 'Assurance'];

export default function Services({ showAll = false }) {
  const [filter, setFilter] = useState('All');
  const filtered = filter === 'All' ? services : services.filter((s) => s.category === filter);
  const visible = showAll ? filtered : filtered.slice(0, 6);

  return (
    <section id="services" className="relative py-20 sm:py-24 lg:py-28 bg-gradient-to-b from-[#0a1b35] via-[#0d2143] to-[#0a1b35] overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-25" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full" style={{ background: 'radial-gradient(ellipse, rgba(232,184,99,0.07) 0%, transparent 70%)' }} />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10">
        <div className="max-w-3xl mb-12 sm:mb-14">
          <p className="text-[#e8b863] tracking-[0.28em] text-[11px] font-semibold uppercase mb-5">
            What we do
          </p>
          <h2 className="font-serif text-white font-medium text-[32px] sm:text-[40px] lg:text-[52px] leading-[1.08] tracking-tight">
            Fifteen specialized service lines.
            <span className="gold-gradient italic"> One integrated team.</span>
          </h2>
          <p className="mt-5 sm:mt-6 text-white/72 text-[15.5px] lg:text-[17px] leading-relaxed">
            From boardroom strategy to back-office execution, we cover the full spectrum of
            services modern organizations need to grow, comply, and outperform.
          </p>
        </div>

        {showAll && (
          <div className="flex flex-wrap items-center gap-2 mb-10 sm:mb-12">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 rounded-full text-[12px] font-medium tracking-wide transition-all duration-300 border ${
                  filter === c
                    ? 'bg-[#e8b863] text-[#0a1b35] border-[#e8b863] shadow-lg shadow-[#e8b863]/20'
                    : 'border-white/18 text-white/78 hover:border-[#e8b863]/55 hover:text-white'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {visible.map((s) => {
            const Icon = iconMap[s.icon] || Calculator;
            return (
              <article
                key={s.num}
                className="group relative bg-gradient-to-br from-white/[0.06] to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-7 hover:border-[#e8b863]/45 transition-all duration-500 hover:-translate-y-1 overflow-hidden"
              >
                <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: 'radial-gradient(circle, rgba(232,184,99,0.22) 0%, transparent 70%)' }} />
                <div className="relative flex items-start justify-between mb-5">
                  <div className="w-12 h-12 rounded-xl bg-[#e8b863]/12 border border-[#e8b863]/30 flex items-center justify-center group-hover:bg-[#e8b863] group-hover:border-[#e8b863] transition-all duration-300">
                    <Icon className="w-5 h-5 text-[#e8b863] group-hover:text-[#0a1b35] transition-colors" strokeWidth={1.5} />
                  </div>
                  <span className="font-serif text-[28px] leading-none text-white/18 group-hover:text-[#e8b863]/45 transition-colors">
                    {s.num}
                  </span>
                </div>
                <p className="text-[10px] text-[#e8b863]/90 tracking-[0.2em] uppercase font-semibold mb-3">
                  {s.category}
                </p>
                <h3 className="text-white font-semibold text-[16.5px] lg:text-[17.5px] leading-snug mb-3">
                  {s.title}
                </h3>
                <p className="text-white/70 text-[13.5px] leading-relaxed line-clamp-3">{s.short}</p>
                <Link
                  to="/services"
                  className="mt-5 inline-flex items-center gap-1.5 text-[#e8b863] text-[12.5px] font-semibold tracking-wide opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  Learn more <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </article>
            );
          })}
        </div>

        {!showAll && (
          <div className="mt-12 text-center">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[#e8b863]/55 text-white text-[14px] font-medium tracking-wide hover:bg-[#e8b863] hover:text-[#0a1b35] transition-all duration-300"
            >
              Explore All 15 Services
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
